import React, { useState, useEffect, useRef } from 'react';
import EfiPay from 'payment-token-efi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stepper,
  Step,
  StepLabel,
  useTheme
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAuth } from '../../contexts/AuthContext';
import { useCredit } from '../../contexts/CreditContext';
import api from '../../services/api';
import { startPaymentPolling } from '../../services/usageService';
import efiPayService, { InstallmentOption } from '../../services/efiPayService';
import UpgradeConfirmationModal from '../../components/UpgradeConfirmationModal';
import TokenIcon from '@mui/icons-material/Token';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { FeedbackWidget } from '../../components/feedback';

interface LocationState {
  amount?: number;
  fromPage?: string;
  method?: 'credit' | 'pix';
  isPlanSubscription?: boolean;
  isAnnual?: boolean;
  planType?: 'basic' | 'professional' | 'expert' | 'unlimited';
  planData?: {
    id: string;
    name: string;
    description: string;
    amount: number;
    features: string[];
  };
}

interface CardData {
  number: string;
  name: string;
  expiry: string;
  cvc: string;
}

interface BillingAddress {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zip_code: string;
  complement?: string;
}

const CheckoutPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUserData } = useAuth();
  const { setShowLowCreditPopup, setCredits } = useCredit();
  const { 
    amount = 0, 
    fromPage = 'usage', 
    method = 'credit',
    isPlanSubscription = false,
    isAnnual = false,
    planType = 'basic',
    planData
  } = (location.state as LocationState) || {};

  const [paymentMethod, setPaymentMethod] = useState<'credit' | 'pix'>(method);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [cardData, setCardData] = useState<CardData>({
    number: '',
    name: '',
    expiry: '',
    cvc: ''
  });
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    zip_code: '',
    complement: ''
  });
  const [loadingCep, setLoadingCep] = useState<boolean>(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{ qr_code: string; qr_code_url: string; expires_at: string } | null>(null);
  const [userCpfCnpj, setUserCpfCnpj] = useState<string>('');
  const [needsCpfCnpj, setNeedsCpfCnpj] = useState<boolean>(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [pollingActive, setPollingActive] = useState<boolean>(false);
  const [pollingSource, setPollingSource] = useState<string>('');
  const [pollingCount, setPollingCount] = useState<number>(0);
  
  // Estados para upgrade de plano
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [currentPlanInfo, setCurrentPlanInfo] = useState<{name: string, amount: number} | null>(null);
  const [newPlanInfo, setNewPlanInfo] = useState<{name: string, amount: number} | null>(null);
  const [upgradeLoading, setUpgradeLoading] = useState<boolean>(false);
  
  // Estados para parcelamento EfiPay
  const [installments, setInstallments] = useState<InstallmentOption[]>([]);
  const [selectedInstallments, setSelectedInstallments] = useState<number>(1);
  const [loadingInstallments, setLoadingInstallments] = useState<boolean>(false);
  const [cardBrand, setCardBrand] = useState<string>('');
  
  // Ref para cancelar polling
  const cancelPollingRef = useRef<(() => void) | null>(null);

  const amountBRL = amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Sem valor escolhido (acesso direto pela URL), não há o que pagar: volta para a escolha de planos/créditos
  useEffect(() => {
    if (!amount) {
      navigate('/usage', { replace: true });
    }
  }, [amount, navigate]);

  // 🚨 FORÇAR FECHAMENTO DE QUALQUER POPUP AO CARREGAR CHECKOUT
  useEffect(() => {

    setShowLowCreditPopup(false);
    
    // Forçar fechamento de qualquer dialog aberto
    const dialogs = document.querySelectorAll('[role="dialog"]');
    dialogs.forEach(dialog => {
      const backdrop = dialog.parentElement;
      if (backdrop && backdrop.classList.contains('MuiBackdrop-root')) {
        (backdrop as HTMLElement).style.display = 'none';
      }
    });
    

  }, [setShowLowCreditPopup]);

  // Função para buscar saldo atual do usuário
  const fetchCurrentBalance = async () => {
    try {
      const response = await api.get('/usage');
      return response.data.available_tokens || 0;
    } catch (error) {
      console.error('Erro ao buscar saldo atual:', error);
      return 0;
    }
  };

  // Função para formatar CEP
  const formatCep = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 5) {
      return cleaned;
    }
    return cleaned.replace(/(\d{5})(\d{1,3})/, '$1-$2');
  };

  // Função para buscar endereço pelo CEP usando ViaCEP
  const fetchAddressByCep = async (cep: string) => {
    const cleanedCep = cep.replace(/\D/g, '');
    
    // Validar formato do CEP
    if (cleanedCep.length !== 8) {
      setCepError('CEP deve conter 8 dígitos');
      return;
    }

    const validCepRegex = /^[0-9]{8}$/;
    if (!validCepRegex.test(cleanedCep)) {
      setCepError('Formato de CEP inválido');
      return;
    }

    setLoadingCep(true);
    setCepError(null);

    try {
      // Limpar campos enquanto busca
      setBillingAddress(prev => ({
        ...prev,
        street: '...',
        neighborhood: '...',
        city: '...',
        state: '...'
      }));

      // Fazer requisição para ViaCEP
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setCepError('CEP não encontrado');
        // Limpar campos em caso de erro
        setBillingAddress(prev => ({
          ...prev,
          street: '',
          neighborhood: '',
          city: '',
          state: ''
        }));
      } else {
        // Preencher campos com dados do ViaCEP
        setBillingAddress(prev => ({
          ...prev,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || ''
        }));
        setCepError(null);
        
        // console.log('📍 Endereço encontrado via ViaCEP:', {
        //   cep: cleanedCep,
        //   logradouro: data.logradouro,
        //   bairro: data.bairro,
        //   cidade: data.localidade,
        //   estado: data.uf
        // });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setCepError('Erro ao consultar CEP. Tente novamente.');
      // Limpar campos em caso de erro
      setBillingAddress(prev => ({
        ...prev,
        street: '',
        neighborhood: '',
        city: '',
        state: ''
      }));
    } finally {
      setLoadingCep(false);
    }
  };

  // Função para lidar com mudança no CEP
  const handleCepChange = (value: string) => {
    const formattedCep = formatCep(value);
    setBillingAddress(prev => ({ ...prev, zip_code: formattedCep }));
    
    // Se o CEP estiver completo, buscar automaticamente
    const cleanedCep = value.replace(/\D/g, '');
    if (cleanedCep.length === 8) {
      fetchAddressByCep(cleanedCep);
    } else {
      setCepError(null);
    }
  };

  // Função para detectar bandeira do cartão
  const detectCardBrand = (cardNumber: string): string => {
    const cleaned = cardNumber.replace(/\D/g, '');
    
    if (cleaned.startsWith('4')) return 'visa';
    if (cleaned.startsWith('5') || cleaned.startsWith('2')) return 'mastercard';
    if (cleaned.startsWith('3')) return 'amex';
    if (cleaned.startsWith('6')) return 'elo';
    
    return '';
  };

  // Função para buscar opções de parcelamento
  const fetchInstallments = async (brand: string) => {
    if (!brand || !isAnnual || !isPlanSubscription) {
      setInstallments([]);
      return;
    }

    setLoadingInstallments(true);
    try {
      const result = await efiPayService.getInstallments(brand, amount);
      setInstallments(result.installments);
    } catch (error) {
      console.error('Erro ao buscar parcelas:', error);
      // Usar parcelas simuladas em caso de erro
      setInstallments(generateSimulatedInstallments());
    } finally {
      setLoadingInstallments(false);
    }
  };

  // Função para gerar parcelas simuladas
  const generateSimulatedInstallments = (): InstallmentOption[] => {
    const options: InstallmentOption[] = [];
    
    for (let i = 1; i <= 12; i++) {
      if (i <= 6) {
        // 1x a 6x sem juros
        options.push({
          installment: i,
          has_interest: false,
          value: Math.round((amount * 100) / i),
          currency: (amount / i).toFixed(2).replace('.', ','),
          interest_percentage: 0
        });
      } else {
        // 7x a 12x com 4.29% ao mês
        const monthlyRate = 0.0429;
        const compoundInterest = Math.pow(1 + monthlyRate, i);
        const totalWithInterest = amount * compoundInterest;
        const installmentValue = Math.round((totalWithInterest * 100) / i);
        
        options.push({
          installment: i,
          has_interest: true,
          value: installmentValue,
          currency: (installmentValue / 100).toFixed(2).replace('.', ','),
          interest_percentage: Math.round((compoundInterest - 1) * 10000)
        });
      }
    }
    
    return options;
  };

  // Função para confirmar upgrade de plano
  const handleUpgradeConfirm = async () => {
    setUpgradeLoading(true);
    setError(null);
    
    try {
// console.log(`💳 [CHECKOUT] Iniciando upgrade de plano...`);
      
      // Fazer a mesma requisição novamente, mas agora o backend deve processar o upgrade
      const subscriptionData: any = {
        plan_id: planType,
        start_date: new Date().toISOString(),
        force_upgrade: true // Flag para indicar que é um upgrade confirmado
      };

      // Incluir dados do cartão (mesmo dados da tentativa anterior)
      const expiry = cardData.expiry.split('/');
      const expMonth = expiry[0];
      let expYear = expiry[1];
      
      if (expYear && expYear.length === 2) {
        expYear = `20${expYear}`;
      }

      subscriptionData.card_data = {
        number: cardData.number.replace(/\s/g, ''),
        name: cardData.name,
        expiry: cardData.expiry,
        exp_month: expMonth,
        exp_year: expYear,
        cvc: cardData.cvc,
        billing_address: {
          street: billingAddress.street,
          number: billingAddress.number,
          neighborhood: billingAddress.neighborhood,
          city: billingAddress.city,
          state: billingAddress.state,
          zip_code: billingAddress.zip_code.replace(/\D/g, ''),
          complement: billingAddress.complement || '',
          line_1: `${billingAddress.street}, ${billingAddress.number}`,
          line_2: billingAddress.complement || '',
          country: 'BR'
        }
      };

      // Fazer requisição para API de subscriptions
      const response = await api.post('/subscriptions/subscribe', subscriptionData);
      const data = response.data;

// console.log(`💳 [CHECKOUT] Upgrade realizado com sucesso:`, data);
      
      // Fechar modal e mostrar sucesso
      setShowUpgradeModal(false);
      setSuccess(true);
      
      // Usuário pode navegar manualmente se desejar
      // Não redirecionar automaticamente
      
    } catch (upgradeErr: any) {
      console.error('Erro no upgrade:', upgradeErr);
      
      let upgradeErrorMessage = 'Erro ao processar upgrade. Tente novamente.';
      if (upgradeErr.response?.data?.detail) {
        upgradeErrorMessage = upgradeErr.response.data.detail;
      }
      
      setError(upgradeErrorMessage);
      setShowUpgradeModal(false);
      setActiveStep(paymentMethod === 'pix' ? 0 : 1);
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Função para cancelar upgrade
  const handleUpgradeCancel = () => {
    setShowUpgradeModal(false);
    setCurrentPlanInfo(null);
    setNewPlanInfo(null);
  };

  // Estados para dados do pagamento bem-sucedido
  const [paymentDetails, setPaymentDetails] = useState<{
    id: string;
    amount: number;
    method: string;
    creditsAdded: number;
    currentBalance: number;
    transactionDate: string;
    status: string;
  } | null>(null);

  // Passos do checkout - diferentes para PIX e Cartão
  const steps = paymentMethod === 'pix' 
    ? ['Informações de Pagamento', 'Processamento', 'PIX - Aguardando Pagamento']
    : ['Informações de Pagamento', 'Confirmação', 'Processamento'];





  // Verificar se o usuário tem CPF/CNPJ cadastrado
  useEffect(() => {
    const checkUserCpfCnpj = async () => {
      // Buscar dados atualizados do usuário via API
      try {
        const response = await api.get('/users/me');
        const userData = response.data;
        const userCpfCnpj = userData.cpf_cnpj;
        
        if (!userCpfCnpj || userCpfCnpj.trim() === '') {
          setNeedsCpfCnpj(true);
        } else {
          setUserCpfCnpj(userCpfCnpj);
          setNeedsCpfCnpj(false);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        // Se houver erro, assumir que precisa do CPF/CNPJ
        setNeedsCpfCnpj(true);
      }
    };
    
    if (user) {
      checkUserCpfCnpj();
    } else {
      setNeedsCpfCnpj(true);
    }
  }, [user]);

  // Limpar polling ao desmontar componente
  useEffect(() => {
    return () => {
      if (cancelPollingRef.current) {
        cancelPollingRef.current();
      }
    };
  }, []);

  // Função para gerar recibo em PDF
  const generateReceipt = () => {
    const receiptContent = `
      ADVOGADA PARCEIRA - COMPROVANTE DE PAGAMENTO
      =============================================
      
      ID da Transação: ${paymentDetails?.id || 'N/A'}
      Data: ${paymentDetails?.transactionDate ? new Date(paymentDetails.transactionDate).toLocaleString('pt-BR') : new Date().toLocaleString('pt-BR')}
      
      DADOS DO PAGAMENTO:
      - Método: ${paymentDetails?.method || (paymentMethod === 'pix' ? 'PIX' : 'Cartão de Crédito')}
      - Valor: ${amountBRL}
      - Status: ${paymentDetails?.status || 'Aprovado'}
      
      CRÉDITOS ADICIONADOS:
      - Quantidade: ${paymentDetails?.creditsAdded || amount} créditos

      
      SALDO ATUAL:
      - Créditos disponíveis: ${paymentDetails?.currentBalance || 'Atualizando...'}
      
      INFORMAÇÕES DA CONTA:
      - Email: ${user?.email || 'N/A'}
             - Nome: ${user?.nome_completo || user?.firstName || 'N/A'}
      
      =============================================
      Este é um comprovante válido de pagamento.
      Em caso de dúvidas, entre em contato conosco.
    `;

    // Criar uma nova janela para impressão
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Comprovante de Pagamento - Advogada Parceira</title>
            <style>
              body { 
                font-family: 'Courier New', monospace; 
                margin: 20px;
                line-height: 1.6;
              }
              .receipt {
                max-width: 600px;
                margin: 0 auto;
                padding: 20px;
                border: 2px solid #333;
                background: #f9f9f9;
              }
              .header {
                text-align: center;
                font-weight: bold;
                margin-bottom: 20px;
                text-transform: uppercase;
              }
              .section {
                margin: 15px 0;
              }
              .section-title {
                font-weight: bold;
                margin-bottom: 5px;
                text-decoration: underline;
              }
              .footer {
                text-align: center;
                margin-top: 30px;
                font-size: 12px;
              }
            </style>
          </head>
          <body>
            <div class="receipt">
              <div class="header">
                <h2>ADVOGADA PARCEIRA</h2>
                <p>COMPROVANTE DE PAGAMENTO</p>
              </div>
              
              <div class="section">
                <div class="section-title">DADOS DA TRANSAÇÃO:</div>
                <p>ID: ${paymentDetails?.id || 'N/A'}</p>
                <p>Data: ${paymentDetails?.transactionDate ? new Date(paymentDetails.transactionDate).toLocaleString('pt-BR') : new Date().toLocaleString('pt-BR')}</p>
              </div>
              
              <div class="section">
                <div class="section-title">DADOS DO PAGAMENTO:</div>
                <p>Método: ${paymentDetails?.method || (paymentMethod === 'pix' ? 'PIX' : 'Cartão de Crédito')}</p>
                <p>Valor: ${amountBRL}</p>
                <p>Status: ${paymentDetails?.status || 'Aprovado'}</p>
              </div>
              
              <div class="section">
                <div class="section-title">CRÉDITOS ADICIONADOS:</div>
                <p>Quantidade: ${paymentDetails?.creditsAdded || amount} créditos</p>

              </div>
              
              <div class="section">
                <div class="section-title">SALDO ATUAL:</div>
                <p>Créditos disponíveis: ${paymentDetails?.currentBalance || 'Atualizando...'}</p>
              </div>
              
              <div class="section">
                <div class="section-title">INFORMAÇÕES DA CONTA:</div>
                <p>Email: ${user?.email || 'N/A'}</p>
                                 <p>Nome: ${user?.nome_completo || user?.firstName || 'N/A'}</p>
              </div>
              
              <div class="footer">
                <p>Este é um comprovante válido de pagamento.</p>
                <p>Em caso de dúvidas, entre em contato conosco.</p>
                <p>Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Iniciar polling quando pagamento for criado
  const startPolling = (paymentId: string) => {
// console.log(`\n🎬 [CHECKOUT] INICIANDO POLLING NO FRONTEND`);
// console.log(`🎬 [CHECKOUT] Payment ID recebido: ${paymentId}`);
// console.log(`🎬 [CHECKOUT] Timestamp: ${new Date().toISOString()}`);
    
    setPaymentId(paymentId);
    setPollingActive(true);
    
// console.log(`🎬 [CHECKOUT] Estados atualizados: paymentId=${paymentId}, pollingActive=true`);
    
    const cancelPolling = startPaymentPolling(
      paymentId,
      async (status, source, details, pollInfo) => {
// console.log(`\n🎬 [CHECKOUT] CALLBACK onStatusUpdate CHAMADO`);
// console.log(`🎬 [CHECKOUT] Status recebido: ${status}`);
// console.log(`🎬 [CHECKOUT] Fonte: ${source}`);
// console.log(`🎬 [CHECKOUT] PollInfo: ${pollInfo ? `${pollInfo.count}/${pollInfo.max}` : 'N/A'}`);
// console.log(`🎬 [CHECKOUT] Details:`, details);
        
        setPaymentStatus(status);
        setPollingSource(source || '');
        if (pollInfo) {
          setPollingCount(pollInfo.count);
// console.log(`🎬 [CHECKOUT] Contador atualizado: ${pollInfo.count}/${pollInfo.max}`);
        }
        
        // Verificar se o pagamento foi concluído
        const paymentStatus = details?.payment_status || status; // Suporte a ambos os formatos
        if (paymentStatus === 'completed') {
// console.log(`\n🎉 [CHECKOUT] PAGAMENTO CONCLUÍDO DETECTADO!`);
// console.log(`🎉 [CHECKOUT] Fonte da aprovação: ${source}`);
// console.log(`🎉 [CHECKOUT] Definindo success=true e pollingActive=false`);
          
          // Buscar o saldo atual do usuário
          const currentBalance = await fetchCurrentBalance();
          
          // Forçar atualização dos dados do usuário
          try {
            // Atualizar dados do usuário no contexto de autenticação
            const updatedUser = await refreshUserData();
            
            // Atualizar também o contexto de créditos imediatamente
            if (updatedUser?.available_credits !== undefined) {
              setCredits(updatedUser.available_credits);
            }
          } catch (refreshError) {
            // Silenciar erro de atualização
          }
          
          // Armazenar detalhes do pagamento para mostrar no comprovante
          setPaymentDetails({
            id: paymentId,
            amount: amount,
            method: paymentMethod === 'pix' ? 'PIX' : 'Cartão de Crédito',
            creditsAdded: details?.tokens_added || amount, // Usar tokens_added se disponível
            currentBalance: currentBalance,
            transactionDate: new Date().toISOString(),
            status: 'Aprovado'
          });
          
          setSuccess(true);
          setPollingActive(false);
          
          // Mostrar mensagem específica dependendo da fonte
          if (source === 'webhook_processed') {
// console.log('🎉 [CHECKOUT] Pagamento aprovado via webhook - mostrando comprovante...');
          } else if (source === 'api_check') {
// console.log('🎉 [CHECKOUT] Pagamento aprovado via consulta API - mostrando comprovante...');
          } else {
// console.log(`🎉 [CHECKOUT] Pagamento aprovado via ${source} - mostrando comprovante...`);
          }
          
          // Não redirecionar automaticamente mais - deixar usuário ver o comprovante
        } else if (paymentStatus === 'failed') {
// console.log(`\n❌ [CHECKOUT] PAGAMENTO FALHOU`);
// console.log(`❌ [CHECKOUT] Fonte: ${source}`);
// console.log(`❌ [CHECKOUT] Parando polling e mostrando erro`);
          
          setPollingActive(false);
          setError('Pagamento falhou. Tente novamente.');
        } else {
// console.log(`🎬 [CHECKOUT] Status ainda pendente: ${status} - continuando polling`);
        }
      },
      () => {
// console.log(`\n✅ [CHECKOUT] CALLBACK onComplete CHAMADO`);
// console.log(`✅ [CHECKOUT] Polling finalizado naturalmente`);
// console.log(`✅ [CHECKOUT] Definindo pollingActive=false`);
        setPollingActive(false);
      },
      (error) => {
// console.log(`\n❌ [CHECKOUT] CALLBACK onError CHAMADO`);
        console.error(`❌ [CHECKOUT] Erro no polling:`, error);
        console.error(`❌ [CHECKOUT] Erro message:`, error.message);
// console.log(`❌ [CHECKOUT] Definindo pollingActive=false e mostrando erro`);
        
        setPollingActive(false);
        setError('Erro ao verificar status do pagamento. Tente atualizar a página.');
      }
    );
    
// console.log(`🎬 [CHECKOUT] Função de cancelamento obtida, armazenando em ref`);
    cancelPollingRef.current = cancelPolling;
// console.log(`🎬 [CHECKOUT] Polling iniciado com sucesso!`);
  };

  // Função para salvar CPF/CNPJ do usuário
  const handleSaveCpfCnpj = async () => {
    if (!userCpfCnpj) {
      setError('CPF/CNPJ é obrigatório para pagamentos');
      return;
    }

    if (!validateCpfCnpj(userCpfCnpj)) {
      setError('CPF/CNPJ inválido. Deve ter 11 ou 14 dígitos.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put('/users/me', { cpf_cnpj: userCpfCnpj });
      
      // Verificar novamente se o CPF/CNPJ foi salvo
      const checkResponse = await api.get('/users/me');
      const updatedUserData = checkResponse.data;
      
      if (updatedUserData.cpf_cnpj && updatedUserData.cpf_cnpj.trim() !== '') {
        setUserCpfCnpj(updatedUserData.cpf_cnpj);
        setNeedsCpfCnpj(false);
      } else {
        setError('Erro ao salvar CPF/CNPJ. Tente novamente.');
        return;
      }
      
      // Mostrar mensagem de sucesso
      alert('CPF/CNPJ salvo com sucesso! Agora você pode prosseguir com o pagamento.');
      
    } catch (err: any) {
      console.error('Erro ao salvar CPF/CNPJ:', err);
      const errorMessage = err.response?.data?.detail || 'Erro ao salvar CPF/CNPJ. Tente novamente.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Função para validar CPF/CNPJ
  const validateCpfCnpj = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    return cleanValue.length === 11 || cleanValue.length === 14;
  };

  // Função para formatar CPF/CNPJ
  const formatCpfCnpj = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 11) {
      return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const handleCardInputChange = (field: keyof CardData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // Formatação básica dos campos
    if (field === 'number') {
      value = value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
      
      // Detectar bandeira do cartão e buscar parcelas (apenas para planos anuais)
      const cleanedNumber = value.replace(/\s/g, '');
      if (cleanedNumber.length >= 6) {
        const brand = detectCardBrand(cleanedNumber);
        setCardBrand(brand);
        
        // Buscar parcelas se for plano anual
        if (brand && isAnnual && isPlanSubscription) {
          fetchInstallments(brand);
        }
      } else {
        setCardBrand('');
        setInstallments([]);
      }
    } else if (field === 'expiry') {
      value = value.replace(/\D/g, '').replace(/(\d{2})(\d{0,2})/, '$1/$2').slice(0, 5);
    } else if (field === 'cvc') {
      value = value.replace(/\D/g, '').slice(0, 3);
    }

    setCardData(prev => ({ ...prev, [field]: value }));
  };

  const handleBillingAddressChange = (field: keyof BillingAddress) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // Formatação específica para CEP
    if (field === 'zip_code') {
      value = value.replace(/\D/g, '').replace(/(\d{5})(\d{0,3})/, '$1-$2').slice(0, 9);
    }

    setBillingAddress(prev => ({ ...prev, [field]: value }));
  };

  const getPlanName = () => {
    if (planData) {
      return planData.name;
    }
    switch (planType) {
      case 'basic':
        return 'Plano Básico';
      case 'professional':
        return 'Plano Profissional';
      case 'expert':
        return 'Plano Especialista';
      case 'unlimited':
        return 'Plano Ilimitado';
      default:
        return 'Plano';
    }
  };

  const getPlanFeatures = () => {
    if (planData && Array.isArray(planData.features)) {
      return planData.features;
    }
    switch (planType) {
      case 'basic':
        return [
          '1.000 tokens/mês',
          '10 documentos/mês',
          'Suporte por email'
        ];
      case 'professional':
        return [
          '5.000 tokens/mês',
          '50 documentos/mês',
          'Suporte prioritário',
          'Recursos avançados de IA'
        ];
      case 'expert':
        return [
          'Tokens ilimitados',
          'Documentos ilimitados',
          'Suporte dedicado 24/7',
          'API personalizada',
          'Recursos exclusivos'
        ];
      case 'unlimited':
        return [
          'Tokens ilimitados',
          'Documentos ilimitados',
          'Suporte dedicado 24/7',
          'API personalizada',
          'Recursos exclusivos'
        ];
      default:
        return ['Plano personalizado', 'Recursos inclusos'];
    }
  };

  const handleProceedToConfirmation = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar se o CPF/CNPJ foi fornecido
    if (needsCpfCnpj) {
      setError('Por favor, preencha e salve seu CPF/CNPJ antes de continuar.');
      return;
    }
    
    // Validar dados do cartão se o método de pagamento for crédito
    if (paymentMethod === 'credit') {
      if (cardData.number.replace(/\s/g, '').length < 16) {
        setError('Número de cartão inválido');
        return;
      }
      
      if (!cardData.name) {
        setError('Nome no cartão é obrigatório');
        return;
      }
      
      if (cardData.expiry.length < 5) {
        setError('Data de validade inválida');
        return;
      }
      
      if (cardData.cvc.length < 3) {
        setError('CVC inválido');
        return;
      }

      // Validar endereço de cobrança
      if (!billingAddress.street) {
        setError('Rua é obrigatória');
        return;
      }
      
      if (!billingAddress.number) {
        setError('Número do endereço é obrigatório');
        return;
      }
      
      if (!billingAddress.neighborhood) {
        setError('Bairro é obrigatório');
        return;
      }
      
      if (!billingAddress.city) {
        setError('Cidade é obrigatória');
        return;
      }
      
      if (!billingAddress.state) {
        setError('Estado é obrigatório');
        return;
      }
      
      if (!billingAddress.zip_code || billingAddress.zip_code.replace(/\D/g, '').length !== 8) {
        setError('CEP inválido');
        return;
      }
    }
    
    setError(null);
    
    // Para PIX, ir direto para processamento (pular confirmação)
    if (paymentMethod === 'pix') {
      handleSubmit();
    } else {
      setActiveStep(1);
    }
  };

  const handleSubmit = async () => {
    // Para PIX, ir para step 1 (processamento), para cartão ir para step 2
    setActiveStep(paymentMethod === 'pix' ? 1 : 2);
    setLoading(true);
    setError(null);

    try {
      // Validação extra: verificar se o CPF/CNPJ foi fornecido
      if (needsCpfCnpj) {
        setError('CPF/CNPJ é obrigatório para processar o pagamento.');
        setActiveStep(paymentMethod === 'pix' ? 0 : 1);
        return;
      }

      // Decidir qual API usar baseado no tipo de pagamento
      if (isPlanSubscription) {
// console.log(`💳 [CHECKOUT] Processando assinatura via EfiPay`);
// console.log(`💳 [CHECKOUT] Plano: ${planType}, Anual: ${isAnnual}, Parcelas: ${selectedInstallments}`);

        let data: any;

        if (isAnnual) {
          // Plano Anual: Usar pagamento único com parcelamento
// console.log(`📅 [CHECKOUT] Plano Anual - Pagamento parcelado em ${selectedInstallments}x`);
          
          // Primeiro, gerar payment_token usando a biblioteca JavaScript da EfiPay
          // (Isso seria feito no frontend com a biblioteca EfiPay)
          const paymentData = {
            amount: amount,
            payment_token: 'MOCK_TOKEN_' + Date.now(), // Em produção, seria gerado pela lib JS
            installments: selectedInstallments,
            cpf: userCpfCnpj,
            billing_address: {
              street: billingAddress.street,
              number: billingAddress.number,
              neighborhood: billingAddress.neighborhood,
              zipcode: billingAddress.zip_code.replace(/\D/g, ''),
              city: billingAddress.city,
              state: billingAddress.state
            }
          };

          // Fazer pagamento único via EfiPay
          const response = await api.post('/payments/efipay/credit-card', paymentData);
          data = response.data;
          
// console.log(`💳 [CHECKOUT] Pagamento anual criado:`, data);
          
        } else {
          // Plano Mensal: Criar assinatura recorrente
// console.log(`🔄 [CHECKOUT] Plano Mensal - Criando assinatura recorrente`);
          
          // Primeiro, criar o plano se não existir
          const planData = {
            name: `Plano ${planType} Mensal`,
            amount: amount,
            interval: 'monthly',
            description: `Assinatura mensal do plano ${planType}`
          };

          const planResponse = await api.post('/payments/efipay/subscription/plan', planData);
          const createdPlan = planResponse.data;
          
// console.log(`📋 [CHECKOUT] Plano criado:`, createdPlan);

          // Gerar payment token real usando EfiPay JavaScript
// console.log(`💳 [CHECKOUT] Gerando payment token real...`);
          
          let paymentToken;
          try {
            // Detectar bandeira do cartão usando a biblioteca EfiPay
            const cardBrand = await EfiPay.CreditCard
              .setCardNumber(cardData.number.replace(/\s/g, ''))
              .verifyCardBrand();
            
// console.log(`🏷️ [CHECKOUT] Bandeira detectada: ${cardBrand}`);
            
            if (cardBrand === 'undefined' || cardBrand === 'unsupported') {
              throw new Error('Cartão não suportado pela EfiPay');
            }
            
            // Gerar token do cartão usando a API correta da EfiPay
            // Identificador de Conta da EfiPay (obtido de: API > Introdução > Identificador de Conta)
            const EFIPAY_PAYEE_CODE = import.meta.env.VITE_EFIPAY_PAYEE_CODE || 'cb5a800f75de4683aea7d758fdc111ae';
            const result = await EfiPay.CreditCard
              .setAccount(EFIPAY_PAYEE_CODE) // Payee code correto
              .setEnvironment('production')
              .setCreditCardData({
                brand: cardBrand,
                number: cardData.number.replace(/\s/g, ''),
                cvv: cardData.cvc,
                expirationMonth: cardData.expiry.split('/')[0].padStart(2, '0'),
                expirationYear: '20' + cardData.expiry.split('/')[1],
                holderName: cardData.name,
                holderDocument: userCpfCnpj.replace(/\D/g, ''),
                reuse: false
              })
              .getPaymentToken();
            
            // Verificar se houve erro
            if ('error' in result) {
              throw new Error(result.error_description || result.error);
            }
            
            paymentToken = result.payment_token;
            
// console.log(`✅ [CHECKOUT] Payment token gerado:`, paymentToken);
// console.log(`🎭 [CHECKOUT] Card mask:`, result.card_mask);
            
            if (!paymentToken || paymentToken.length !== 40) {
              throw new Error('Payment token inválido gerado pela EfiPay');
            }
            
          } catch (tokenError: any) {
            console.error(`❌ [CHECKOUT] Erro ao gerar payment token:`, tokenError);
            const errorMessage = tokenError?.error_description || tokenError?.message || 'Erro desconhecido';
            throw new Error(`Erro ao processar cartão: ${errorMessage}`);
          }
          
          // Agora criar a assinatura
          const subscriptionData = {
            plan_id: createdPlan.plan_id,
            payment_token: paymentToken, // Token real da EfiPay
            amount: amount, // Adicionar valor do plano
            cpf: userCpfCnpj,
            billing_address: {
              street: billingAddress.street,
              number: billingAddress.number,
              neighborhood: billingAddress.neighborhood,
              zipcode: billingAddress.zip_code.replace(/\D/g, ''),
              city: billingAddress.city,
              state: billingAddress.state,
              complement: billingAddress.complement
            }
          };

          const response = await api.post('/payments/efipay/subscription', subscriptionData);
          data = response.data;
          
// console.log(`🔄 [CHECKOUT] Assinatura criada:`, data);
        }

// console.log(`\n💳 [CHECKOUT] RESPOSTA DA API EFIPAY RECEBIDA`);
// console.log(`💳 [CHECKOUT] Success: ${data.success}`);
// console.log(`💳 [CHECKOUT] Dados completos:`, data);

        if (data.success) {
// console.log(`💳 [CHECKOUT] Resposta recebida da EfiPay`);
// console.log(`💳 [CHECKOUT] Status real do pagamento:`, data.status);
          
          // ⚠️ CRÍTICO: SÓ MOSTRAR SUCESSO SE O PAGAMENTO FOI REALMENTE APROVADO
          const realStatus = data.status || 'unknown';
          const approvedStatuses = ['paid', 'captured', 'approved', 'completed', 'active'];
          
          if (approvedStatuses.includes(realStatus.toLowerCase())) {
// console.log(`✅ [CHECKOUT] Pagamento REALMENTE aprovado - Status: ${realStatus}`);
            
            const currentBalance = await fetchCurrentBalance();
            
            if (isAnnual) {
              setPaymentDetails({
                id: data.payment_id || data.charge_id,
                amount: data.amount,
                method: `Cartão de Crédito (${selectedInstallments}x)`,
                creditsAdded: Math.floor(amount),
                currentBalance: currentBalance,
                transactionDate: new Date().toISOString(),
                status: 'Aprovado'
              });
            } else {
              setPaymentDetails({
                id: data.subscription_id,
                amount: data.amount || amount,
                method: 'Assinatura Mensal (Cartão)',
                creditsAdded: data.credits_added ?? Math.floor(amount),
                currentBalance: currentBalance,
                transactionDate: new Date().toISOString(),
                status: 'Ativo'
              });
            }
            
            setSuccess(true);
          } else {
// console.log(`⚠️ [CHECKOUT] Pagamento NÃO aprovado - Status: ${realStatus}`);
// console.log(`⚠️ [CHECKOUT] Iniciando polling para aguardar aprovação...`);
            
            // Iniciar polling para aguardar aprovação real
            if (data.payment_id || data.charge_id || data.subscription_id) {
              const paymentId = data.payment_id || data.charge_id || data.subscription_id;
              setPaymentId(paymentId);
              startPolling(paymentId);
            } else {
              throw new Error('ID do pagamento não retornado pela EfiPay');
            }
          }
        } else {
          throw new Error(data.message || 'Erro ao processar pagamento');
        }
      } else {
        // Para pagamentos avulsos, usar API de usage/credits
        const paymentData: any = {
          amount: amount,
          payment_method: paymentMethod
        };

        // Se for cartão de crédito, gerar payment_token usando EfiPay
        if (paymentMethod === 'credit') {
          console.log('💳 [CHECKOUT] Gerando payment token para pagamento avulso...');
          
          let paymentToken;
          try {
            // Detectar bandeira do cartão usando a biblioteca EfiPay
            const cardBrand = await EfiPay.CreditCard
              .setCardNumber(cardData.number.replace(/\s/g, ''))
              .verifyCardBrand();
            
            console.log(`🏷️ [CHECKOUT] Bandeira detectada: ${cardBrand}`);
            
            if (cardBrand === 'undefined' || cardBrand === 'unsupported') {
              throw new Error('Cartão não suportado pela EfiPay');
            }
            
            // Extrair dados do cartão
            const expiry = cardData.expiry.split('/');
            const expMonth = expiry[0];
            let expYear = expiry[1];
            
            // Converter ano de 2 dígitos para 4 dígitos se necessário
            if (expYear && expYear.length === 2) {
              expYear = `20${expYear}`;
            }
            
            // Gerar token do cartão usando a API da EfiPay
            // Usar o mesmo Payee Code das assinaturas
            const EFIPAY_PAYEE_CODE = import.meta.env.VITE_EFIPAY_PAYEE_CODE || 'cb5a800f75de4683aea7d758fdc111ae';
            const result = await EfiPay.CreditCard
              .setAccount(EFIPAY_PAYEE_CODE)
              .setEnvironment('production')
              .setCreditCardData({
                brand: cardBrand,
                number: cardData.number.replace(/\s/g, ''),
                cvv: cardData.cvc,
                expirationMonth: expMonth,
                expirationYear: expYear,
                holderName: cardData.name,
                holderDocument: userCpfCnpj.replace(/\D/g, ''),
                reuse: false
              })
              .getPaymentToken();
            
            // Verificar se houve erro
            if ('error' in result) {
              throw new Error(result.error_description || result.error);
            }
            
            paymentToken = result.payment_token;
            
            console.log(`✅ [CHECKOUT] Payment token gerado:`, paymentToken);
            console.log(`🎭 [CHECKOUT] Card mask:`, result.card_mask);
            
            if (!paymentToken || paymentToken.length !== 40) {
              throw new Error('Payment token inválido gerado pela EfiPay');
            }
            
          } catch (tokenError: any) {
            console.error('❌ Erro ao gerar payment token:', tokenError);
            throw new Error(`Erro ao processar cartão: ${tokenError.message || 'Token inválido'}`);
          }

          // Enviar payment_token e dados do endereço
          paymentData.card_data = {
            payment_token: paymentToken,
            installments: selectedInstallments,
            billing_address: {
              street: billingAddress.street,
              number: billingAddress.number,
              neighborhood: billingAddress.neighborhood,
              city: billingAddress.city,
              state: billingAddress.state,
              zipcode: billingAddress.zip_code.replace(/\D/g, ''),
              complement: billingAddress.complement || ''
            }
          };
        }

        // Fazer requisição para a API usando o serviço
        const response = await api.post('/usage/credits', paymentData);
        const data = response.data;

        // Verificar o status do pagamento
// console.log(`\n💳 [CHECKOUT] RESPOSTA DA API RECEBIDA`);
// console.log(`💳 [CHECKOUT] Status: ${data.status}`);
// console.log(`💳 [CHECKOUT] Payment ID: ${data.id}`);
// console.log(`💳 [CHECKOUT] Payment Method: ${paymentMethod}`);
// console.log(`💳 [CHECKOUT] Provider: ${data.payment_provider || 'não informado'}`);
// console.log(`💳 [CHECKOUT] Payment Type: ${data.payment_type || 'não informado'}`);
// console.log(`💳 [CHECKOUT] Campos PIX disponíveis:`, {
//   pix_qr_code: !!data.pix_qr_code,
//   pix_qr_code_url: !!data.pix_qr_code_url,
//   qr_code: !!data.qr_code,
//   qr_code_image: !!data.qr_code_image,
//   pix_copy_paste: !!data.pix_copy_paste,
//   expires_at: !!data.expires_at
// });
// console.log(`💳 [CHECKOUT] Dados completos:`, data);
        
        if (data.status === 'completed') {
// console.log(`💳 [CHECKOUT] Pagamento já aprovado - mostrando confirmação`);
          setSuccess(true);
          // Usuário pode navegar manualmente se desejar
          // Não redirecionar automaticamente
        } else if (data.status === 'pending' && paymentMethod === 'pix') {
// console.log(`💳 [CHECKOUT] PIX pendente - configurando QR Code e iniciando polling`);
// console.log(`💳 [CHECKOUT] Dados recebidos da API:`, data);
          
          // Para PIX, mostrar QR Code e aguardar confirmação
          // Verificar diferentes formatos de retorno (EfiPay, Pagarme, MercadoPago)
          const pixDataToSet = {
            qr_code: data.pix_qr_code || data.qr_code || data.pix_copy_paste || '',
            qr_code_url: data.pix_qr_code_url || data.qr_code_image || data.qr_code_url || '',
            expires_at: data.expires_at || ''
          };
          
// console.log(`💳 [CHECKOUT] PIX Data processado:`, pixDataToSet);
// console.log(`💳 [CHECKOUT] Provider usado: ${data.payment_provider || 'desconhecido'}`);
          
          // Validar se temos dados mínimos para mostrar o PIX
          if (!pixDataToSet.qr_code && !pixDataToSet.qr_code_url) {
            console.error(`💳 [CHECKOUT] ERRO: PIX sem QR Code nem URL!`);
            console.error(`💳 [CHECKOUT] Dados disponíveis:`, Object.keys(data));
            throw new Error('PIX gerado mas sem dados de QR Code. Verifique a integração.');
          }
          
          setPixData(pixDataToSet);
          setActiveStep(paymentMethod === 'pix' ? 2 : 3); // PIX vai para step 2, cartão para step 3
          
          // Iniciar polling para verificar status do pagamento PIX
          if (data.id) {
// console.log(`💳 [CHECKOUT] Iniciando polling para PIX com ID: ${data.id}`);
            startPolling(data.id);
          } else {
            console.error(`💳 [CHECKOUT] ERRO: PIX sem payment ID!`);
          }
        } else if (data.status === 'pending' && paymentMethod === 'credit') {
// console.log(`💳 [CHECKOUT] Cartão pendente - iniciando polling`);
          
          // Para cartão, aguardar confirmação via polling
          setActiveStep(3);
          
          // Iniciar polling para verificar status do pagamento
          if (data.id) {
// console.log(`💳 [CHECKOUT] Iniciando polling para Cartão com ID: ${data.id}`);
            startPolling(data.id);
          } else {
            console.error(`💳 [CHECKOUT] ERRO: Cartão sem payment ID!`);
          }
        } else if (data.status === 'failed') {
          const errorMessage = paymentMethod === 'pix' 
            ? 'Erro ao gerar PIX. Tente novamente ou use cartão de crédito.'
            : 'Pagamento não foi aprovado. Verifique os dados do cartão.';
          throw new Error(errorMessage);
        } else {
          // ⚠️ CRÍTICO: NUNCA mostrar sucesso para status pendente/desconhecido
// console.log(`⚠️ [CHECKOUT] Status não aprovado: ${data.status}`);
// console.log(`⚠️ [CHECKOUT] Iniciando polling para aguardar aprovação real...`);
          
          // Iniciar polling para aguardar aprovação
          if (data.payment_id || data.id) {
            const paymentId = data.payment_id || data.id;
            setPaymentId(paymentId);
            startPolling(paymentId);
          } else {
            throw new Error('Pagamento criado mas não foi aprovado. Status: ' + (data.status || 'desconhecido'));
          }
        }
      }

    } catch (err: any) {
      console.error('Erro no pagamento:', err);
      
      // Extrair mensagem de erro da API
      let errorMessage = 'Erro ao processar pagamento. Por favor, tente novamente.';
      
      if (err.response?.data?.detail) {
        // Usar a mensagem específica da API
        errorMessage = err.response.data.detail;
        
        // Verificar se é um erro de assinatura ativa e se é uma assinatura
        if (isPlanSubscription && errorMessage.includes('assinatura ativa')) {
// console.log(`💳 [CHECKOUT] Detectado erro de assinatura ativa para upgrade`);
          
          // Tentar extrair informações dos planos da mensagem de erro
          const currentPlanMatch = errorMessage.match(/R\$\s*([\d,]+\.?\d*)/);
          const currentPlanValue = currentPlanMatch ? parseFloat(currentPlanMatch[1].replace(',', '')) : 0;
          
          // Obter informações do novo plano
          const planNames: Record<string, string> = {
            'basic': 'Plano Básico',
            'professional': 'Plano Profissional', 
            'expert': 'Plano Expert',
            'unlimited': 'Plano Ilimitado'
          };
          
          const planPrices: Record<string, number> = {
            'basic': 49.90,
            'professional': 99.90,
            'expert': 189.90,
            'unlimited': 299.90
          };
          
          const newPlanValue = planPrices[planType] || amount;
          const newPlanName = planNames[planType] || `Plano ${planType}`;
          
          // Verificar se realmente é um upgrade (novo plano > atual)
          if (newPlanValue > currentPlanValue) {
            setCurrentPlanInfo({
              name: `Plano Atual`,
              amount: currentPlanValue
            });
            setNewPlanInfo({
              name: newPlanName,
              amount: newPlanValue
            });
            setShowUpgradeModal(true);
            setActiveStep(paymentMethod === 'pix' ? 0 : 1);
            setLoading(false);
            return; // Não mostrar erro, mostrar modal de upgrade
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
// console.log(`💳 [CHECKOUT] Erro capturado: ${errorMessage}`);
      setError(errorMessage);
      setActiveStep(paymentMethod === 'pix' ? 0 : 1);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          {/* Cabeçalho de Sucesso */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            textAlign: 'center',
            mb: 4
          }}>
            <Box sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 120,
              height: 120,
              borderRadius: '50%',
              bgcolor: 'success.main',
              mb: 3,
              boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)'
            }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>
            
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                mb: 2
              }}
            >
              Pagamento Confirmado
            </Typography>
            
            <Typography 
              variant="h6" 
              color="text.secondary" 
              paragraph
              sx={{ 
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Sua transação foi processada com sucesso e os créditos foram adicionados à sua conta
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Comprovante Detalhado */}
          <Grid container spacing={4}>
            {/* Coluna Esquerda - Detalhes do Pagamento */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  height: '100%',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <ReceiptIcon sx={{ color: 'white' }} />
                  </Box>
                  <Typography variant="h6" fontWeight="600">
                    Detalhes da Transação
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    ID da Transação
                  </Typography>
                  <Typography variant="body2" fontWeight="500" sx={{ fontFamily: 'monospace' }}>
                    {paymentDetails?.id?.slice(-12) || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Data e Hora
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {paymentDetails?.transactionDate 
                      ? new Date(paymentDetails.transactionDate).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : new Date().toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric', 
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                    }
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Método
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {paymentDetails?.method || (paymentMethod === 'pix' ? 'PIX' : 'Cartão de Crédito')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Valor
                  </Typography>
                  <Typography variant="h6" color="success.main" fontWeight="700">
                    {amountBRL}
                  </Typography>
                </Box>

                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'success.light', 
                  borderRadius: 2,
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" color="success.dark" fontWeight="600">
                    ✓ Pagamento Aprovado
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Coluna Direita - Créditos e Saldo */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  height: '100%',
                  background: 'linear-gradient(145deg, #e8f5e8 0%, #f1f8e9 100%)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <TokenIcon sx={{ color: 'white' }} />
                  </Box>
                  <Typography variant="h6" fontWeight="600">
                    Seus Créditos
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Créditos Adicionados
                  </Typography>
                  <Typography 
                    variant="h2" 
                    sx={{ 
                      fontWeight: 800,
                      background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1
                    }}
                  >
                    +{paymentDetails?.creditsAdded || amount}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontWeight="500">
                    créditos
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(255,255,255,0.8)', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'success.light'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Saldo Anterior
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {(paymentDetails?.currentBalance || user?.available_credits || 0) - (paymentDetails?.creditsAdded || amount)} créditos
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Créditos Adicionados
                    </Typography>
                    <Typography variant="body2" fontWeight="500" color="success.main">
                      +{paymentDetails?.creditsAdded || amount} créditos
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="600">
                      Saldo Atual
                    </Typography>
                    <Typography variant="h6" color="primary.main" fontWeight="700">
                      {paymentDetails?.currentBalance || user?.available_credits || 0} créditos
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" textAlign="center" gutterBottom fontWeight="600" color="text.primary">
              O que você pode fazer agora?
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2, maxWidth: 800, mx: 'auto' }}>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<ChatBubbleIcon />}
                  onClick={() => navigate('/chat')}
                  sx={{ 
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)'
                  }}
                >
                  Iniciar Chat
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Converse com a IA jurídica
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<DescriptionIcon />}
                  onClick={() => navigate('/documents')}
                  sx={{ 
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 }
                  }}
                >
                  Gerar Documentos
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Crie petições e contratos
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PrintIcon />}
                  onClick={generateReceipt}
                  sx={{ 
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 }
                  }}
                >
                  Baixar Comprovante
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Para seus registros
                </Typography>
              </Grid>
            </Grid>

            {/* Footer da Confirmação */}
            <Box sx={{ 
              mt: 6, 
              p: 3, 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Obrigado por escolher a <strong>Advogada Parceira</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Em caso de dúvidas, entre em contato conosco através do suporte
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Este comprovante comprova o pagamento realizado. Guarde-o para seus registros.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Para dúvidas, entre em contato com nosso suporte através do chat
              </Typography>
            </Box>
          </Box>
        </Paper>
        
        {/* Feedback após pagamento */}
        <FeedbackWidget
          feature="general"
          entityType="payment"
          entityId={paymentDetails?.id}
          title="Como foi sua experiência de pagamento?"
          position="bottom-right"
          compact
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Voltar
            </Button>
            <Typography variant="h5" component="h1" fontWeight="medium">
              Checkout
            </Typography>
          </Box>
          <Chip 
            label="Pagamento Seguro" 
            color="success" 
            variant="outlined"
            sx={{ fontWeight: 'medium' }}
          />
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Resumo da compra */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={5}>
            <Card elevation={3} sx={{ 
              borderRadius: 2,
              bgcolor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <CardHeader 
                title="Resumo da Compra" 
                sx={{ 
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  py: 1.5
                }}
              />
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  {isPlanSubscription ? (
                    <>
                      <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
                        {getPlanName()}
                      </Typography>
                      <Typography variant="subtitle1">
                        Valor mensal: <Box component="span" fontWeight="bold">{amountBRL}</Box>
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                        Recursos incluídos:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {getPlanFeatures().map((feature, index) => (
                          <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Assinatura com renovação automática mensal
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Adição de Créditos
                      </Typography>
                      <Typography variant="subtitle1">
                        Quantidade: <Box component="span" fontWeight="bold">{amount} créditos</Box>
                      </Typography>
                      <Typography variant="subtitle1">
                        Valor: <Box component="span" fontWeight="bold">{amountBRL}</Box>
                      </Typography>
                      <Divider sx={{ my: 2 }} />

                    </>
                  )}
                </Box>
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: theme.palette.grey[50], 
                  borderRadius: 1, 
                  border: `1px dashed ${theme.palette.grey[300]}`
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
                    Conta associada: {user?.email || 'Usuário'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            {activeStep === 0 && (
              <form onSubmit={handleProceedToConfirmation}>
                {/* CPF/CNPJ */}
                {needsCpfCnpj && (
                  <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="h6" gutterBottom color="text.primary">
                      CPF/CNPJ Obrigatório
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Para processar pagamentos em produção, é necessário informar seu CPF ou CNPJ. Preencha o campo abaixo e clique em "Salvar e Continuar".
                    </Typography>
                    <TextField
                      label="CPF ou CNPJ"
                      variant="outlined"
                      fullWidth
                      value={userCpfCnpj}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 14) {
                          setUserCpfCnpj(formatCpfCnpj(value));
                        }
                      }}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      error={userCpfCnpj ? !validateCpfCnpj(userCpfCnpj) : false}
                      helperText={userCpfCnpj && !validateCpfCnpj(userCpfCnpj) ? "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos" : undefined}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSaveCpfCnpj}
                      disabled={loading || !userCpfCnpj || !validateCpfCnpj(userCpfCnpj)}
                      fullWidth
                    >
                      {loading ? <CircularProgress size={24} /> : 'Salvar e Continuar'}
                    </Button>
                  </Box>
                )}

                {/* ENDEREÇO DE COBRANÇA */}
                {!needsCpfCnpj && paymentMethod === 'credit' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                      Endereço de Cobrança
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Grid container spacing={2}>
                      {/* CEP - Primeiro campo */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="CEP"
                          value={billingAddress.zip_code}
                          onChange={(e) => handleCepChange(e.target.value)}
                          placeholder="00000-000"
                          required
                          variant="outlined"
                          error={!!cepError}
                          helperText={cepError || "Digite o CEP para preencher automaticamente"}
                          InputProps={{
                            endAdornment: loadingCep ? <CircularProgress size={20} /> : null
                          }}
                        />
                      </Grid>
                      
                      {/* Número */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Número"
                          value={billingAddress.number}
                          onChange={handleBillingAddressChange('number')}
                          placeholder="123"
                          required
                          variant="outlined"
                        />
                      </Grid>
                      
                      {/* Rua */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Rua"
                          value={billingAddress.street}
                          onChange={handleBillingAddressChange('street')}
                          placeholder="Nome da rua (preenchido automaticamente)"
                          required
                          variant="outlined"
                          disabled={loadingCep}
                        />
                      </Grid>
                      
                      {/* Bairro */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Bairro"
                          value={billingAddress.neighborhood}
                          onChange={handleBillingAddressChange('neighborhood')}
                          placeholder="Nome do bairro (preenchido automaticamente)"
                          required
                          variant="outlined"
                          disabled={loadingCep}
                        />
                      </Grid>
                      
                      {/* Complemento */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Complemento"
                          value={billingAddress.complement}
                          onChange={handleBillingAddressChange('complement')}
                          placeholder="Apto, sala, etc. (opcional)"
                          variant="outlined"
                        />
                      </Grid>
                      
                      {/* Cidade */}
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="Cidade"
                          value={billingAddress.city}
                          onChange={handleBillingAddressChange('city')}
                          placeholder="Nome da cidade (preenchido automaticamente)"
                          required
                          variant="outlined"
                          disabled={loadingCep}
                        />
                      </Grid>
                      
                      {/* Estado */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Estado"
                          value={billingAddress.state}
                          onChange={handleBillingAddressChange('state')}
                          placeholder="SP"
                          required
                          variant="outlined"
                          disabled={loadingCep}
                          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                        />
                      </Grid>
                    </Grid>
                    
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Dica:</strong> Digite o CEP e os campos serão preenchidos automaticamente
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {/* Método de Pagamento */}
                {!needsCpfCnpj && (
                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <FormLabel component="legend">Método de Pagamento</FormLabel>
                    <RadioGroup
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as 'credit' | 'pix')}
                    >
                      <FormControlLabel 
                        value="credit" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CreditCardIcon sx={{ mr: 1 }} />
                            Cartão de Crédito
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="pix" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <QrCode2Icon sx={{ mr: 1 }} />
                            PIX
                          </Box>
                        }
                        disabled={isPlanSubscription}
                      />
                      {isPlanSubscription && paymentMethod === 'pix' && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', ml: 2 }}>
                          Assinaturas requerem pagamento por cartão de crédito para renovação automática.
                        </Typography>
                      )}
                    </RadioGroup>
                  </FormControl>
                )}

                {/* DADOS DO CARTÃO */}
                {!needsCpfCnpj && paymentMethod === 'credit' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                      Dados do Cartão
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Número do Cartão"
                          value={cardData.number}
                          onChange={handleCardInputChange('number')}
                          placeholder="0000 0000 0000 0000"
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Nome no Cartão"
                          value={cardData.name}
                          onChange={handleCardInputChange('name')}
                          placeholder="Como aparece no cartão"
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Validade (MM/AA)"
                          value={cardData.expiry}
                          onChange={handleCardInputChange('expiry')}
                          placeholder="MM/AA"
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="CVC"
                          value={cardData.cvc}
                          onChange={handleCardInputChange('cvc')}
                          placeholder="123"
                          required
                          variant="outlined"
                        />
                      </Grid>

                      {/* Informações do Plano e Parcelamento */}
                      {isPlanSubscription && (
                        <Grid item xs={12}>
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
                              Informações do Plano
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2">
                                {planData?.name || 'Plano Selecionado'}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {amountBRL}
                              </Typography>
                            </Box>
                            
                            {isAnnual ? (
                              <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
                                Plano Anual - Parcelamento disponível
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium' }}>
                                Plano Mensal - Cobrança recorrente a cada 30 dias
                              </Typography>
                            )}
                            
                            {/* Mostrar bandeira do cartão */}
                            {cardBrand && (
                              <Box sx={{ mt: 1 }}>
                                <Chip 
                                  label={`Bandeira: ${cardBrand.toUpperCase()}`} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined" 
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      )}

                      {/* Seletor de Parcelas - Apenas para Planos Anuais */}
                      {isPlanSubscription && isAnnual && installments.length > 0 && (
                        <Grid item xs={12}>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
                              Forma de Pagamento
                            </FormLabel>
                            <RadioGroup
                              value={selectedInstallments.toString()}
                              onChange={(e) => setSelectedInstallments(parseInt(e.target.value))}
                            >
                              {installments.map((installment) => (
                                <FormControlLabel
                                  key={installment.installment}
                                  value={installment.installment.toString()}
                                  control={<Radio />}
                                  label={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                      <Box>
                                        <Typography variant="body2">
                                          {installment.installment}x de R$ {installment.currency}
                                        </Typography>
                                        {installment.has_interest && (
                                          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'medium' }}>
                                            Com juros de 4,29% a.m.
                                          </Typography>
                                        )}
                                      </Box>
                                      {!installment.has_interest && installment.installment <= 6 && (
                                        <Chip label="Sem Juros" size="small" color="success" />
                                      )}
                                    </Box>
                                  }
                                  sx={{ 
                                    border: '1px solid', 
                                    borderColor: 'grey.300',
                                    borderRadius: 1,
                                    mb: 1,
                                    p: 1,
                                    '&:hover': {
                                      bgcolor: 'grey.50'
                                    }
                                  }}
                                />
                              ))}
                            </RadioGroup>
                            
                            {loadingInstallments && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="caption">
                                  Carregando opções de parcelamento...
                                </Typography>
                              </Box>
                            )}
                          </FormControl>
                        </Grid>
                      )}

                    </Grid>
                  </Box>
                )}

                {/* QR Code do PIX */}
                {!needsCpfCnpj && paymentMethod === 'pix' && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" gutterBottom>
                      Escaneie o QR Code para pagar
                    </Typography>
                    <Box 
                      sx={{ 
                        width: 200, 
                        height: 200, 
                        bgcolor: 'grey.100', 
                        mx: 'auto', 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1
                      }}
                    >
                      <QrCode2Icon sx={{ fontSize: 100, color: 'grey.500' }} />
                    </Box>

                    <Box sx={{ mt: 2, bgcolor: theme.palette.grey[100], p: 2, borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Após realizar o pagamento via PIX, seus créditos serão adicionados automaticamente à sua conta em até 5 minutos.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {error && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || needsCpfCnpj}
                  >
                    {needsCpfCnpj ? 'Preencha CPF/CNPJ primeiro' : (paymentMethod === 'pix' ? 'Gerar PIX' : 'Continuar')}
                  </Button>
                </Box>
              </form>
            )}

            {activeStep === 1 && paymentMethod === 'credit' && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Confirmar Pagamento
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: theme.palette.grey[50] }}>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    Detalhes do pagamento:
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Valor:</Typography>
                    <Typography variant="body2" fontWeight="bold">{amountBRL}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Método:</Typography>
                    <Typography variant="body2">
                      {paymentMethod === 'credit' 
                        ? `Cartão de crédito (final ${cardData.number.replace(/\s/g, '').slice(-4)})` 
                        : 'PIX'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Produto:</Typography>
                    <Typography variant="body2">
                      {isPlanSubscription ? getPlanName() : `${amount} créditos`}
                    </Typography>
                  </Box>
                </Paper>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Ao clicar em "Confirmar Pagamento", você concorda com os termos de uso e política de privacidade da Advogada Parceira.
                  </Typography>
                </Alert>

                {error && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(0)}
                    disabled={loading}
                  >
                    Voltar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                  >
                    {loading ? 'Processando...' : 'Confirmar Pagamento'}
                  </Button>
                </Box>
              </Box>
            )}

            {((activeStep === 1 && paymentMethod === 'pix') || (activeStep === 2 && paymentMethod === 'credit')) && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {paymentMethod === 'pix' ? 'Gerando QR Code PIX' : 'Processando seu pagamento'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {paymentMethod === 'pix' 
                    ? 'Criando cobrança PIX e gerando QR Code. Isso pode levar alguns segundos...'
                    : 'Por favor, aguarde enquanto processamos sua transação...'
                  }
                </Typography>
                {paymentMethod === 'pix' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    ⏱️ Tempo estimado: 5-10 segundos
                  </Typography>
                )}
              </Box>
            )}

            {((activeStep === 2 && paymentMethod === 'pix') || (activeStep === 3 && paymentMethod === 'credit')) && pixData && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  PIX Gerado com Sucesso!
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Escaneie o QR Code abaixo ou copie o código PIX para efetuar o pagamento:
                </Typography>

                {pixData.qr_code_url && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <img 
                      src={pixData.qr_code_url} 
                      alt="QR Code PIX" 
                      style={{ 
                        maxWidth: '250px', 
                        width: '100%',
                        border: '1px solid #ddd',
                        borderRadius: '8px'
                      }}
                      onError={(e) => {
                        console.error('Erro ao carregar QR Code:', pixData.qr_code_url);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
                
                {!pixData.qr_code_url && pixData.qr_code && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      QR Code não disponível para visualização, mas você pode copiar o código PIX abaixo:
                    </Typography>
                  </Box>
                )}

                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Código PIX (Copiar e Colar):
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-all', 
                      fontFamily: 'monospace',
                      fontSize: '0.8rem'
                    }}
                  >
                    {pixData.qr_code}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigator.clipboard.writeText(pixData.qr_code)}
                    sx={{ mt: 1 }}
                  >
                    Copiar Código PIX
                  </Button>
                </Paper>

                {/* Status do Pagamento */}
                {pollingActive && (
                  <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <CircularProgress size={24} sx={{ mr: 1.5 }} />
                      <Typography variant="body1" color="text.primary" fontWeight="medium">
                        Verificando status do pagamento...
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Status: <strong>
                          {paymentStatus === 'pending' ? 'Aguardando pagamento' : 
                           paymentStatus === 'completed' ? 'Pagamento aprovado' :
                           paymentStatus === 'failed' ? 'Pagamento falhou' : paymentStatus}
                        </strong>
                      </Typography>
                      
                      {pollingCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Verificação {pollingCount}/200 • A cada 3 segundos • Máximo 10 minutos
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )}

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Após realizar o pagamento, seus créditos serão adicionados automaticamente à sua conta.
                    O PIX expira em {new Date(pixData.expires_at).toLocaleString()}.
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  {!pollingActive && paymentStatus === 'pending' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => paymentId && startPolling(paymentId)}
                    >
                      Continuar Verificando
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/usage')}
                  >
                    Ir para Área de Consumo
                  </Button>

                </Box>
              </Box>
            )}

            {((activeStep === 2 && paymentMethod === 'credit') || (activeStep === 3 && paymentMethod === 'credit')) && !pixData && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Aguardando Confirmação do Pagamento
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Estamos verificando o status do seu pagamento...
                </Typography>

                {/* Status do Pagamento */}
                {pollingActive && (
                  <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <CircularProgress size={24} sx={{ mr: 1.5 }} />
                      <Typography variant="body1" color="text.primary" fontWeight="medium">
                        Verificando status do pagamento...
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Status: <strong>
                          {paymentStatus === 'pending' ? 'Aguardando confirmação' : 
                           paymentStatus === 'completed' ? 'Pagamento aprovado' :
                           paymentStatus === 'failed' ? 'Pagamento falhou' : paymentStatus}
                        </strong>
                      </Typography>
                      
                      {pollingCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Verificação {pollingCount}/200 • A cada 3 segundos • Máximo 10 minutos
                        </Typography>
                      )}
                      
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Esta verificação é automática, não feche esta página.
                      </Typography>
                    </Box>
                  </Paper>
                )}

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Assim que o pagamento for confirmado, você será redirecionado automaticamente
                    e seus créditos serão adicionados à sua conta.
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  {!pollingActive && paymentStatus === 'pending' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => paymentId && startPolling(paymentId)}
                    >
                      Continuar Verificando
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/usage')}
                  >
                    Ir para Área de Consumo
                  </Button>

                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Modal de Upgrade de Plano */}
      {showUpgradeModal && currentPlanInfo && newPlanInfo && (
        <UpgradeConfirmationModal
          open={showUpgradeModal}
          onClose={handleUpgradeCancel}
          onConfirm={handleUpgradeConfirm}
          currentPlan={currentPlanInfo}
          newPlan={newPlanInfo}
          loading={upgradeLoading}
        />
      )}
    </Container>
  );
};

export default CheckoutPage; 